import {
  Box,
  Container,
  Header,
  Link,
  SpaceBetween,
  TextContent,
} from '@amzn/awsui-components-core';
import {
  ContentQualityQuestionData,
  ContentQualityResponseOptions,
  ContentType,
  QuestionResponse,
} from '@amzn/awscat-pattern-library-service-model';
import React from 'react';
import QuestionnaireQuestionItem from './QuestionnaireQuestionItem';
import {
  BR_REVIEW_GUIDELINES_PATTERN_LINK,
  BR_REVIEW_GUIDELINES_GUIDE_LINK,
  BR_REVIEW_GUIDELINES_STRATEGY_LINK,
} from '../utils/ContentQualityUtils';

interface Props {
  category: string;
  sequences: number[]; // question sequences that belong to this category
  sequenceToQuestionMap: Map<number, ContentQualityQuestionData>;
  sequenceToVisibilityMap?: Map<number, boolean>;
  sequenceToResponseMap: Map<number, QuestionResponse>;
  handleResponseUpdated?: (
    sequence: number,
    response: ContentQualityResponseOptions,
    comment: string,
  ) => void;
  readonly?: boolean;
  contentType?: ContentType;
}

const QuestionnaireCategoryBox: React.FunctionComponent<Props> = (props: Props) => {
  const {
    category,
    sequences,
    sequenceToQuestionMap,
    sequenceToVisibilityMap,
    sequenceToResponseMap,
    handleResponseUpdated,
    readonly,
    contentType,
  } = props;

  const questions = sequences?.map((sequence) => {
    const question = sequenceToQuestionMap.get(sequence) as ContentQualityQuestionData;
    const shouldDisplay = readonly ? true : sequenceToVisibilityMap?.get(sequence) || false;
    return !!question ? (
      <QuestionnaireQuestionItem
        key={sequence}
        question={question}
        response={sequenceToResponseMap?.get(sequence)?.response || ''}
        comment={sequenceToResponseMap?.get(sequence)?.comment || ''}
        shouldDisplay={shouldDisplay}
        handleResponseUpdated={handleResponseUpdated}
        readonly={readonly}
      />
    ) : null;
  });

  const contentTypeToGuideLinkMap = new Map<ContentType, string>([
    [ContentType.Guide, BR_REVIEW_GUIDELINES_GUIDE_LINK],
    [ContentType.Strategy, BR_REVIEW_GUIDELINES_STRATEGY_LINK],
    [ContentType.Pattern, BR_REVIEW_GUIDELINES_PATTERN_LINK],
  ]);

  const guidelinesLink =
    category === 'FIT' ? (
      <span>
        <Link
          variant="secondary"
          href={contentTypeToGuideLinkMap.get(contentType!)}
          target="_blank"
        >
          <Box variant="h3" color="text-status-info">
            Bar Raiser Review Guidelines - {contentType}
          </Box>
        </Link>
      </span>
    ) : null;

  const readonlyCategoryBox = (
    <Box margin={'s'}>
      <Box variant="p" fontWeight="bold">
        {category}
      </Box>
      {questions}
    </Box>
  );

  const editableCategoryBox = (
    <Container
      header={
        <Header
          variant="h3"
          actions={
            <SpaceBetween direction="horizontal" size="s">
              {guidelinesLink}
            </SpaceBetween>
          }
        >
          {category}
        </Header>
      }
    >
      {questions}
    </Container>
  );

  return !readonly ? editableCategoryBox : readonlyCategoryBox;
};

export default QuestionnaireCategoryBox;
